import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

import editorVideo from '../../../assets/video/touchpose_editor.mp4';
import VideoPlayer from './VideoPlayer';
import imgTouchSetEditor from '../../../assets/images/docs/editor-edit-touchpose.png';
import imgSetsDescription from '../../../assets/images/docs/editor-sets-description.png';
import imgEditMode from '../../../assets/images/docs/editor-edit-mode.png';
import imgCreateSets from '../../../assets/images/docs/editor-create-sets.png.jpg';
import imgAddFaces from '../../../assets/images/docs/editor-add-faces.png';
import imgRemoveFaces from '../../../assets/images/docs/editor-remove-faces.png';
import imgMirrorSets from '../../../assets/images/docs/editor-mirror-sets.png';
import imgProjectSets from '../../../assets/images/docs/editor-project-sets.png';
import imgBuildTouchpose from '../../../assets/images/docs/editor-build-touchpose.png';
import imgFindSets from '../../../assets/images/docs/editor-find-sets.png';
import imgExportImport from '../../../assets/images/docs/editor-export-import-sets.png';
import imgLayersDropdown from '../../../assets/images/docs/editor-layers-dropdown.png';
import imgGroups from '../../../assets/images/docs/editor-groups.png';
import imgSetColors from '../../../assets/images/docs/editor-set-colors.png';
import imgPreferences from '../../../assets/images/docs/editor-preferences.png';
import { Body, MenuPath, Note, SectionDivider, Tip, Warning } from '../ui';

// ── Callout components ─────────────────────────────────────────────────────

const FullImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <div className="my-[24px]">
    <img
      src={src}
      alt={alt}
      className="w-full rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
    />
  </div>
);

// Icon + title + content row — matches the original page layout
const SectionRow: React.FC<{
  icon: string;
  iconAlt: string;
  title: string;
  children: React.ReactNode;
}> = ({ icon, iconAlt, title, children }) => (
  <div className="flex gap-[24px] mb-[36px]">
    <img
      src={icon}
      alt={iconAlt}
      className="w-[80px] h-[80px] shrink-0 rounded-[8px] object-cover"
    />
    <div className="flex-1">
      <h2 className="font-display text-[18px] font-bold text-content-primary mb-[8px] tracking-[-0.2px]">
        {title}
      </h2>
      {children}
    </div>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────

const TouchposeEditorDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary">
      {/* -- Overview -- */}
      <h2
        id="overview"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.editor.overviewTitle')}
      </h2>
      <VideoPlayer src={editorVideo} />

      <SectionDivider />

      {/* -- Touch Set Editor overview -- */}
      <h2
        id="touch-set-editor"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.editor.touchSetEditorTitle')}
      </h2>
      <div className="my-[24px]">
        <img
          src={imgTouchSetEditor}
          alt={t('docs.editor.imgTouchSetEditorAlt')}
          className="block max-w-full w-auto rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
        />
      </div>

      <SectionDivider />

      {/* -- What are Touch Sets? -- */}
      <SectionRow
        icon={imgSetsDescription}
        iconAlt={t('docs.editor.whatAreTouchSetsTitle')}
        title={t('docs.editor.whatAreTouchSetsTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.whatAreTouchSetsBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
      </SectionRow>

      {/* -- Edit TouchPose -- */}
      <SectionRow
        icon={imgEditMode}
        iconAlt={t('docs.editor.editTouchPoseTitle')}
        title={t('docs.editor.editTouchPoseTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.editTouchPoseBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
      </SectionRow>

      <SectionDivider />

      {/* -- Create Touch Sets -- */}
      <SectionRow
        icon={imgCreateSets}
        iconAlt={t('docs.editor.createTouchSetsTitle')}
        title={t('docs.editor.createTouchSetsTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.createTouchSetsBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
        <Note label={t('docs.editor.noteLabel')}>
          {t('docs.editor.createTouchSetsNote')}
        </Note>
      </SectionRow>

      {/* -- Add Faces to Sets -- */}
      <SectionRow
        icon={imgAddFaces}
        iconAlt={t('docs.editor.addFacesTitle')}
        title={t('docs.editor.addFacesTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.addFacesBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
        <Note label={t('docs.editor.noteLabel')}>
          {t('docs.editor.addFacesNote')}
        </Note>
      </SectionRow>

      {/* -- Remove Faces from Sets -- */}
      <SectionRow
        icon={imgRemoveFaces}
        iconAlt={t('docs.editor.removeFacesTitle')}
        title={t('docs.editor.removeFacesTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.removeFacesBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
      </SectionRow>

      {/* -- Mirroring Sets -- */}
      <SectionRow
        icon={imgMirrorSets}
        iconAlt={t('docs.editor.mirrorSetsTitle')}
        title={t('docs.editor.mirrorSetsTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.mirrorSetsBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
        <Warning label={t('docs.editor.warningLabel')}>
          <ul className="mt-[4px] pl-[18px]">
            <li className="mb-[4px]">
              {t('docs.editor.mirrorWarningItem1')}
            </li>
            <li className="mb-[4px]">
              {t('docs.editor.mirrorWarningItem2')}
            </li>
            <li>
              <Trans
                i18nKey="docs.editor.mirrorWarningItem3"
                components={{ code: <MenuPath>{''}</MenuPath> }}
              />
            </li>
          </ul>
        </Warning>
      </SectionRow>

      {/* -- Projecting Sets -- */}
      <SectionRow
        icon={imgProjectSets}
        iconAlt={t('docs.editor.projectSetsTitle')}
        title={t('docs.editor.projectSetsTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.projectSetsBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
        <Tip label={t('docs.editor.tipLabel')}>
          {t('docs.editor.projectSetsTip')}
        </Tip>
      </SectionRow>

      {/* -- Build TouchPose -- */}
      <SectionRow
        icon={imgBuildTouchpose}
        iconAlt={t('docs.editor.buildTouchPoseTitle')}
        title={t('docs.editor.buildTouchPoseTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.buildTouchPoseBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
        <Note label={t('docs.editor.noteLabel')}>
          <ul className="mt-[4px] pl-[18px]">
            <li>
              <Trans
                i18nKey="docs.editor.buildTouchPoseNote"
                components={{ code: <MenuPath>{''}</MenuPath> }}
              />
            </li>
          </ul>
        </Note>
        <Tip label={t('docs.editor.tipLabel')}>
          {t('docs.editor.buildTouchPoseTip')}
        </Tip>
      </SectionRow>

      {/* -- Find Set from Selection -- */}
      <SectionRow
        icon={imgFindSets}
        iconAlt={t('docs.editor.findSetTitle')}
        title={t('docs.editor.findSetTitle')}
      >
        <Body>{t('docs.editor.findSetBody')}</Body>
      </SectionRow>

      {/* -- File Menu: Open / Save / New / Recent -- */}
      <SectionRow
        icon={imgExportImport}
        iconAlt={t('docs.editor.fileMenuTitle')}
        title={t('docs.editor.fileMenuTitle')}
      >
        {/* This string carries a <code> span, so it needs <Trans>, not t() —
            with t() the tag rendered as literal text on the page. */}
        <Body>
          <Trans
            i18nKey="docs.editor.fileMenuBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
        <ul className="text-[14px] leading-[1.75] text-content-secondary mb-[12px] pl-[20px]">
          <li>
            <Trans
              i18nKey="docs.editor.fileMenuOpen"
              components={{ code: <MenuPath>{''}</MenuPath>, strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="docs.editor.fileMenuSave"
              components={{ code: <MenuPath>{''}</MenuPath>, strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="docs.editor.fileMenuSaveAs"
              components={{ code: <MenuPath>{''}</MenuPath>, strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="docs.editor.fileMenuRecent"
              components={{ code: <MenuPath>{''}</MenuPath>, strong: <strong /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="docs.editor.fileMenuNew"
              components={{ code: <MenuPath>{''}</MenuPath>, strong: <strong /> }}
            />
          </li>
        </ul>
        <Note label={t('docs.editor.noteLabel')}>
          {t('docs.editor.fileMenuOpenNote')}
        </Note>
      </SectionRow>

      {/* -- Exporting / Importing -- */}
      <SectionRow
        icon={imgExportImport}
        iconAlt={t('docs.editor.exportImportTitle')}
        title={t('docs.editor.exportImportTitle')}
      >
        <Body>
          <Trans
            i18nKey="docs.editor.exportImportBody"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </Body>
        <Warning label={t('docs.editor.warningLabel')}>
          {t('docs.editor.exportImportWarning')}
        </Warning>
      </SectionRow>

      <SectionDivider />

      {/* -- Layers -- */}
      <h2
        id="layers"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[8px] tracking-[-0.4px]"
      >
        {t('docs.editor.layersTitle')}
      </h2>
      <Body>{t('docs.editor.layersBody')}</Body>
      <Tip label={t('docs.editor.tipLabel')}>{t('docs.editor.layersTip')}</Tip>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.managingLayersTitle')}
      </h3>
      <Body>{t('docs.editor.managingLayersBody')}</Body>
      <div className="my-[16px]">
        <img
          src={imgLayersDropdown}
          alt={t('docs.editor.layersDropdownAlt')}
          className="block max-w-[420px] rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
        />
      </div>
      <ul className="pl-[18px] mb-[16px] font-body text-[14px] text-content-primary leading-[1.7]">
        <li className="mb-[6px]">
          <Trans i18nKey="docs.editor.layerAddItem" components={{ strong: <strong /> }} />
        </li>
        <li className="mb-[6px]">
          <Trans i18nKey="docs.editor.layerRenameItem" components={{ strong: <strong /> }} />
        </li>
        <li className="mb-[6px]">
          <Trans i18nKey="docs.editor.layerDeleteItem" components={{ strong: <strong /> }} />
        </li>
      </ul>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.copySetsTitle')}
      </h3>
      <Body>{t('docs.editor.copySetsBody')}</Body>

      <Note label={t('docs.editor.noteLabel')}>{t('docs.editor.layersNote')}</Note>

      <SectionDivider />

      {/* -- Groups -- */}
      <h2
        id="groups"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[8px] tracking-[-0.4px]"
      >
        {t('docs.editor.groupsTitle')}
      </h2>
      <div className="my-[24px]">
        <img
          src={imgGroups}
          alt={t('docs.editor.groupsImgAlt')}
          className="block max-w-[480px] rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
        />
      </div>
      <Body>{t('docs.editor.groupsBody')}</Body>
      <Tip label={t('docs.editor.tipLabel')}>{t('docs.editor.groupsTip')}</Tip>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.creatingGroupsTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.editor.creatingGroupsBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <ul className="pl-[18px] mb-[16px] font-body text-[14px] text-content-primary leading-[1.7]">
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.creatingGroupsItem1"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.creatingGroupsItem2"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.creatingGroupsItem3"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.organizingGroupsTitle')}
      </h3>
      <Body>{t('docs.editor.organizingGroupsBody')}</Body>
      <ul className="pl-[18px] mb-[16px] font-body text-[14px] text-content-primary leading-[1.7]">
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.organizingItemAssign"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.organizingItemUnassign"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.organizingItemNest"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.organizingItemRename"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.organizingItemDelete"
            components={{ strong: <strong /> }}
          />
        </li>
        <li className="mb-[6px]">
          <Trans
            i18nKey="docs.editor.organizingItemColor"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.groupsHotkeysTitle')}
      </h3>
      <Body>{t('docs.editor.groupsHotkeysBody')}</Body>

      <table className="w-full border-collapse text-[13px] mb-[20px]">
        <thead>
          <tr className="border-b-2 border-surface-hover">
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary w-[160px]">
              {t('docs.editor.groupsHotkeysShortcutCol')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary w-[200px]">
              {t('docs.editor.groupsHotkeysActionCol')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.editor.groupsHotkeysDescCol')}
            </th>
          </tr>
        </thead>
        <tbody>
          {([
            ['Ctrl+G', 'docs.editor.hotkeyCreateGroup', 'docs.editor.hotkeyCreateGroupDesc'],
            ['Ctrl+P', 'docs.editor.hotkeyParentToGroup', 'docs.editor.hotkeyParentToGroupDesc'],
            ['Ctrl+Alt+P', 'docs.editor.hotkeyParentFromMaya', 'docs.editor.hotkeyParentFromMayaDesc'],
            ['Shift+P', 'docs.editor.hotkeyUnparent', 'docs.editor.hotkeyUnparentDesc'],
            ['Delete / Backspace', 'docs.editor.hotkeyDelete', 'docs.editor.hotkeyDeleteDesc'],
            ['Enter / Return', 'docs.editor.hotkeyRename', 'docs.editor.hotkeyRenameDesc'],
            ['Ctrl+A', 'docs.editor.hotkeySelectAll', 'docs.editor.hotkeySelectAllDesc'],
            ['Ctrl+F', 'docs.editor.hotkeyFind', 'docs.editor.hotkeyFindDesc'],
            ['Shift+F', 'docs.editor.hotkeyScrollTo', 'docs.editor.hotkeyScrollToDesc'],
          ] as Array<[string, string, string]>).map(([keys, actionKey, descKey], i) => (
            <tr
              key={i}
              className={`border-b border-surface-secondary ${i % 2 === 0 ? 'bg-surface-base' : 'bg-surface'}`}
            >
              <td className="px-[12px] py-[8px]">
                <MenuPath>{keys}</MenuPath>
              </td>
              <td className="px-[12px] py-[8px] text-content-secondary">
                {t(actionKey)}
              </td>
              <td className="px-[12px] py-[8px] text-slate-500 text-[12px]">
                {t(descKey)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Body>
        <Trans
          i18nKey="docs.editor.groupsMenuBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.editor.groupsRightClickBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.groupsLayersTitle')}
      </h3>
      <Body>{t('docs.editor.groupsLayersBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.groupsMirroringTitle')}
      </h3>
      <Body>{t('docs.editor.groupsMirroringBody')}</Body>

      <Note label={t('docs.editor.noteLabel')}>{t('docs.editor.groupsNote')}</Note>

      <SectionDivider />

      {/* -- TouchPose Colors -- */}
      <h2
        id="touchpose-colors"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[8px] tracking-[-0.4px]"
      >
        {t('docs.editor.colorsTitle')}
      </h2>
      <div className="my-[24px]">
        <img
          src={imgSetColors}
          alt="TouchPose Colors panel"
          className="block max-w-[480px] rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
         />
      </div>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.changingColorsTitle')}
      </h3>
      <Body>{t('docs.editor.changingColorsBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[16px] mb-[6px]">
        {t('docs.editor.highlightColorsTitle')}
      </h3>
      <Body>{t('docs.editor.highlightColorsBody1')}</Body>
      <Body>{t('docs.editor.highlightColorsBody2')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[16px] mb-[6px]">
        {t('docs.editor.selectionColorsTitle')}
      </h3>
      <Body>{t('docs.editor.selectionColorsBody')}</Body>
      <Note label={t('docs.editor.noteLabel')}>
        <ul className="mt-[4px] pl-[18px]">
          <li className="mb-[4px]">
            {t('docs.editor.selectionColorsNote1')}
          </li>
          <li>{t('docs.editor.selectionColorsNote2')}</li>
        </ul>
      </Note>

      <SectionDivider />

      {/* -- Preferences -- */}
      <h2
        id="preferences"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[8px] tracking-[-0.4px]"
      >
        {t('docs.editor.preferencesTitle')}
      </h2>
      <div className="my-[24px]">
        <img
          src={imgPreferences}
          alt="Touch Preferences panel"
          className="block max-w-[380px] rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
        />
      </div>
      <Body>
        <Trans
          i18nKey="docs.editor.preferencesBody"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.editor.mainTouchSetTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.editor.mainTouchSetBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[16px] mb-[6px]">
        {t('docs.editor.mirrorTokensTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.editor.mirrorTokensBody"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Note label={t('docs.editor.noteLabel')}>
        <Trans
          i18nKey="docs.editor.mirrorTokensNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[16px] mb-[6px]">
        {t('docs.editor.previewOptionsTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.editor.previewAfterApplyBody"
          components={{ strong: <strong /> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.editor.previewControlNamesBody"
          components={{ strong: <strong /> }}
        />
      </Body>
    </article>
  );
};

export default TouchposeEditorDoc;
